<script setup lang="ts">
import type { ContactFormSectionEmits, ContactFormSectionProps } from '.';

import { useContactFormAutofill } from '~/composables/contactForm';
import { useEvents } from '~/composables/events/useEvents';
import { useAgentProfileLink } from '~/composables/useAgentProfileNavigation';
import { usePropertyContext } from '~/composables/usePropertyContext';
import { MixpanelProps } from '~/core/service/mixpanel';
import { hasShortsVideo } from '~/utils';

import { BrokerAvatarInfo } from '~/components/BrokerAvatarInfo';
import { InputPhone } from '~/components/InputPhone';
import { ToggleContainer } from '~/components/ToggleContainer';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';

const props = defineProps<ContactFormSectionProps>();

const emit = defineEmits<ContactFormSectionEmits>();

// Destructure grouped props for easier access
const {
	formData,
	validation,
	formAttributes,
	brokerInfo,
	textContent,
	appState
} = toRefs(props);

const { trackCtaViewMoreClicked, trackFormPropertyInitiated } = useEvents();

const route = useRoute();

// Try to get property context for tracking, with fallback
// Only attempt to use PropertyContext if we're likely in a property context
const propertyContext = (() => {
	// Check if we're in a property context before attempting injection
	const isLikelyPropertyContext = route.path.includes('/imovel/');

	if (!isLikelyPropertyContext) {
		return null;
	}

	try {
		return usePropertyContext();
	} catch {
		return null;
	}
})();

// Function to determine current page context more reliably
function getCurrentPageContext(): string {
	// 1. First, try to use the property context directly if available
	if (propertyContext?.property?.value) {
		return 'Detalhe do Imóvel';
	}

	// 2. Check the current route to determine context
	if (route.path.startsWith('/shorts/')) {
		return route.fullPath;
	}
	if (route.path.includes('/imovel/')) {
		return 'Detalhe do Imóvel';
	}
	if (route.path.includes('/condominio/')) {
		return 'Detalhe do Condomínio';
	}

	// 3. Fall back to appState (but with logging for debugging)
	const contextFromAppState = appState.value.isPropertyContext
		? 'Detalhe do Imóvel'
		: 'Detalhe do Condomínio';

	return contextFromAppState;
}

function shortsRouteCommercialId(): string {
	if (!route.path.startsWith('/shorts/')) return '';
	// Typed routes union does not narrow `params` from `path`; shorts uses `commercialId`.
	const params = route.params as { commercialId?: string | string[] };
	const raw = params.commercialId;
	if (raw === undefined) return '';
	if (Array.isArray(raw)) return raw[0] ?? '';
	return typeof raw === 'string' ? raw : '';
}

function getTrackedCommercialId(): string {
	return (
		props.analyticsData?.propertyCommercialId ||
		propertyContext?.property?.value?.commercialId ||
		shortsRouteCommercialId()
	);
}

function getTrackedAskingPrice(): number | null {
	return (
		props.analyticsData?.askingPrice ??
		propertyContext?.property?.value?.askingPrice ??
		null
	);
}

function getTrackedVideoShorts(): boolean {
	if (props.analyticsData?.videoShorts !== undefined) {
		return props.analyticsData.videoShorts;
	}

	return hasShortsVideo(propertyContext?.property?.value);
}

const throttledTrackCtaViewMoreClicked = useThrottleFn(() => {
	// More robust context determination
	const currentPageContext = getCurrentPageContext();

	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]:
			textContent.value.expandText || 'Mostrar mais informações',
		[MixpanelProps.CURRENT_PAGE]: currentPageContext,
		[MixpanelProps.CURRENT_FLOW]: 'lead form',
		[MixpanelProps.COMMERCIAL_ID]: getTrackedCommercialId()
	});
}, 1000);

const { agentProfileLinkProps } = useAgentProfileLink(
	() => brokerInfo.value.broker
);

const shouldShowAgentProfileLink = computed(
	() => appState.value.showAgentProfileLink ?? false
);

const shouldShowBrokerInfo = computed(
	() => appState.value.showBrokerInfo ?? appState.value.isPropertyContext
);

// Track if form initiated event was already sent
const formInitiatedEventSent = ref(false);

// Form field models
const name = computed({
	get: () => formData.value.name,
	set: (value: string) => emit('update:formData', { name: value })
});

const phone = computed({
	get: () => formData.value.phone,
	set: (value: string) => emit('update:formData', { phone: value })
});

const email = computed({
	get: () => formData.value.email,
	set: (value: string) => emit('update:formData', { email: value })
});

const userMessage = computed({
	get: () => formData.value.userMessage,
	set: (value: string) => emit('update:formData', { userMessage: value })
});

// Template refs
const expandableContainer = useTemplateRef('expandableContainer');
const firstFormInput = useTemplateRef('firstFormInput');
const phoneInput = useTemplateRef('phoneInput');
const emailInput = useTemplateRef('emailInput');
const userMessageInput = useTemplateRef('userMessageInput');
const contactForm = useTemplateRef<HTMLFormElement>('contactForm');

const { observeAutofill, syncAutofilledFields, detectBrowserContext } =
	useContactFormAutofill();

observeAutofill(contactForm);

onMounted(detectBrowserContext);

// Methods
function handleSubmit() {
	syncContactFormAutofill();
	emit('submit');
}

function syncContactFormAutofill() {
	syncAutofilledFields(contactForm.value);
}

function handleToggleChange(isExpanded: boolean) {
	emit('toggle-expanded', isExpanded);

	// Track mixpanel event when expanding the form (throttled)
	if (isExpanded) {
		throttledTrackCtaViewMoreClicked();
	}
}

function handleInputClick(inputType: 'name' | 'phone' | 'email' | 'message') {
	// Only send the event once
	if (formInitiatedEventSent.value) {
		return;
	}

	formInitiatedEventSent.value = true;

	// Build click tracking parameters
	const clickParams = {
		name: inputType === 'name',
		phone: inputType === 'phone',
		email: inputType === 'email',
		message: inputType === 'message'
	};

	trackFormPropertyInitiated({
		[MixpanelProps.COMMERCIAL_ID]: getTrackedCommercialId(),
		[MixpanelProps.ASKING_PRICE]: getTrackedAskingPrice(),
		[MixpanelProps.COMPANY_NAME]: brokerInfo.value.companyName || '',
		[MixpanelProps.AGENT_NAME]: brokerInfo.value.name || '',
		[MixpanelProps.ACCOUNT_STATUS]: appState.value.isUserLoggedIn
			? 'logged in'
			: 'sign up',
		[MixpanelProps.CURRENT_PAGE]: getCurrentPageContext(),
		[MixpanelProps.CURRENT_FLOW]: 'lead form',
		[MixpanelProps.CLICK_NAME]: clickParams.name,
		[MixpanelProps.CLICK_PHONE]: clickParams.phone,
		[MixpanelProps.CLICK_EMAIL]: clickParams.email,
		[MixpanelProps.CLICK_MESSAGE]: clickParams.message,
		[MixpanelProps.INPUT_NAME]: formData.value.name || '',
		[MixpanelProps.INPUT_PHONE]: formData.value.phone || '',
		[MixpanelProps.INPUT_EMAIL]: formData.value.email || '',
		[MixpanelProps.INPUT_MESSAGE]: formData.value.userMessage || '',
		[MixpanelProps.VIDEO_SHORTS]: getTrackedVideoShorts()
	});
}

async function validateFirstEmptyFieldToFocus() {
	await nextTick();

	if (!name.value?.trim()) {
		firstFormInput.value?.focus();
		return;
	}

	// For phone, check both the modelValue and the actual number from InputPhone
	// The InputPhone component exposes a 'number' property that contains just the digits
	const phoneValue = phone.value?.trim();
	const phoneNumber = phoneInput.value?.number?.trim();
	const isPhoneEmpty =
		!phoneValue ||
		phoneValue.match(/^\+\d{1,4}$/) || // Just country code pattern (+55, +1, etc.)
		!phoneNumber; // Check the actual number from InputPhone (without country code)

	if (isPhoneEmpty) {
		const phoneInputElement = phoneInput.value?.$el?.querySelector('input');
		phoneInputElement?.focus();
		return;
	}

	if (!email.value?.trim()) {
		emailInput.value?.focus();
		return;
	}

	userMessageInput.value?.focus();
}

function expandContainer() {
	if (!expandableContainer.value?.isExpanded) {
		expandableContainer.value?.toggle();
	}
}

// Expose methods
defineExpose({
	validateFirstEmptyFieldToFocus,
	expandContainer,
	syncAutofilledFields: syncContactFormAutofill
});
</script>

<template>
	<div :class="props.class">
		<!-- Broker Info Section -->
		<section
			v-if="shouldShowBrokerInfo"
			data-test-id="contact-form-section-broker-info"
			class="overflow-hidden"
		>
			<BrokerAvatarInfo
				v-bind="shouldShowAgentProfileLink ? agentProfileLinkProps : {}"
				:border="false"
				avatar-shape="circle"
				company-avatar-shape="circle"
				:show-company-avatar="true"
				sizes="linear"
				:broker-src="brokerInfo.avatarSrc || ''"
				:company-src="brokerInfo.companyLogo || ''"
				avatar-class="h-11 w-11"
				:info-classes="{
					wrapper: 'ml-3 min-w-0',
					brokerName:
						'text-primary font-matter-sq text-base font-normal leading-[150%] tracking-[-0.32px] truncate pl-3',
					companyName:
						'text-primary-light-2 font-normal text-xs leading-[150%] truncate pl-3'
				}"
				:broker-name="brokerInfo.name"
				:company-name="brokerInfo.companyName"
				class="max-w-[300px] gap-3 md:max-w-[400px] lg:max-w-[450px]"
			>
				<template #broker>{{ brokerInfo.name }}</template>
				<template #company>
					{{ brokerInfo.atText }}
				</template>
			</BrokerAvatarInfo>

			<div class="pb-4 pt-6">
				<p
					class="text-sm font-normal leading-[150%] text-primary 2xl:text-base"
				>
					{{ brokerInfo.message }}
				</p>
			</div>
		</section>

		<!-- Contact Form - ClientOnly to prevent user data from being cached -->
		<ClientOnly>
			<form
				id="contact-form-section"
				ref="contactForm"
				class="flex flex-col gap-3"
				@submit.prevent="handleSubmit"
			>
				<Input
					ref="firstFormInput"
					v-model.trim="name"
					v-bind="formAttributes?.nameAttrs"
					cy-test-id="pdp-name-input"
					:error="validation.errors.name"
					type="text"
					name="name"
					autocomplete="name"
					:placeholder="textContent.namePlaceholder"
					class="h-[3.125rem] bg-white text-primary"
					@click="handleInputClick('name')"
				/>

				<InputPhone
					ref="phoneInput"
					v-model.trim="phone"
					v-bind="formAttributes?.phoneAttrs"
					:error="validation.errors.phone"
					:disabled="validation.isPhoneDisabled"
					cy-test-id="pdp-phone-input"
					name="phone"
					autocomplete="tel"
					class="bg-white text-primary"
					@click="handleInputClick('phone')"
				/>

				<ToggleContainer
					ref="expandableContainer"
					container-class="flex flex-col gap-3 "
					content-class="flex flex-col gap-3"
					:expand-text="textContent.expandText"
					:collapse-text="textContent.collapseText"
					:default-expanded="props.defaultExpanded"
					@update:model-value="handleToggleChange"
				>
					<template #content>
						<Input
							ref="emailInput"
							v-model.trim="email"
							v-bind="formAttributes?.emailAttrs"
							cy-test-id="pdp-mail-input"
							type="email"
							name="email"
							autocomplete="email"
							inputmode="email"
							:placeholder="textContent.emailPlaceholder"
							:error="validation.errors.email"
							class="h-[3.125rem] bg-white text-primary"
							@click="handleInputClick('email')"
						/>

						<Textarea
							ref="userMessageInput"
							v-model.trim="userMessage"
							v-bind="formAttributes?.userMessageAttrs"
							data-testid="user-message-textarea"
							:placeholder="textContent.userMessagePlaceholder"
							:error="validation.errors.userMessage"
							class="min-h-[5.625rem] resize-none bg-white text-primary"
							@click="handleInputClick('message')"
						/>
					</template>
				</ToggleContainer>
			</form>

			<template #fallback>
				<form class="flex flex-col gap-3">
					<Input
						name="name"
						autocomplete="name"
						:placeholder="textContent.namePlaceholder"
						class="h-[3.125rem] bg-white text-primary"
					/>
					<InputPhone
						model-value=""
						name="phone"
						autocomplete="tel"
						class="bg-white text-primary"
					/>
				</form>
			</template>
		</ClientOnly>
	</div>
</template>
